import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";
import LogoImage from "../../assets/logo/1template.png";

interface WithClassName {
    className?: string;
    pathClassName?: string;
}

const getGradientColours = () => {
    const primaryFrom =
        getComputedStyle(document.documentElement).getPropertyValue(
            "--gradient-primary-from",
        ) || "#4F46E5";
    const primaryTo =
        getComputedStyle(document.documentElement).getPropertyValue(
            "--gradient-primary-to",
        ) || "#7C3AED";
    return { primaryFrom, primaryTo };
};

export const SmartMatchIcon = ({
    className,
    gradient = false,
}: WithClassName & { gradient?: boolean }) => {
    // Linear gradient IDs must be unique in every instance
    const gradientId1 = `paint0_linear_${uuidv4()}`;
    const gradientId2 = `paint1_linear_${uuidv4()}`;
    const gradientId3 = `paint2_linear_${uuidv4()}`;
    const { primaryFrom, primaryTo } = getGradientColours();

    return gradient ? (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <defs>
                <linearGradient
                    id={gradientId1}
                    x1="1.99996"
                    y1="2.26949"
                    x2="6.54541"
                    y2="6.81494"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
                <linearGradient
                    id={gradientId2}
                    x1="17.9896"
                    y1="4.63312"
                    x2="20.1714"
                    y2="19.7593"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
                <linearGradient
                    id={gradientId3}
                    x1="5.32617"
                    y1="12.0025"
                    x2="18.6636"
                    y2="12.0025"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
            </defs>
            <path
                d="M2.04351 4.60472L2.73905 4.86123C3.30217 5.06889 3.74607 5.51276 3.95369 6.07584L4.21023 6.77143C4.21988 6.79757 4.24481 6.81494 4.27268 6.81494C4.30056 6.81494 4.32549 6.79757 4.33513 6.77143L4.59168 6.07588C4.79934 5.51276 5.24324 5.06889 5.80631 4.86123L6.50189 4.60472C6.52804 4.59508 6.54541 4.57015 6.54541 4.54227C6.54541 4.5144 6.52804 4.48947 6.50189 4.47982L5.80627 4.22323C5.2432 4.01557 4.79934 3.57171 4.59168 3.00863L4.33513 2.313C4.32549 2.28685 4.30056 2.26949 4.27268 2.26949C4.24481 2.26949 4.21988 2.28685 4.21023 2.313L3.95369 3.00863C3.74603 3.57171 3.30217 4.01561 2.73909 4.22323L2.04347 4.47982C2.01732 4.48947 1.99996 4.5144 1.99996 4.54227C1.99996 4.57015 2.01736 4.59508 2.04351 4.60472Z"
                fill={`url(#${gradientId1})`}
            />
            <path
                d="M18.0105 5.75404L18.3443 5.87716C18.6146 5.97684 18.8277 6.18989 18.9274 6.46017L19.0505 6.79405C19.0551 6.80661 19.0671 6.81494 19.0805 6.81494C19.0939 6.81494 19.1058 6.80661 19.1105 6.79405L19.2336 6.46019C19.3333 6.18989 19.5463 5.97684 19.8166 5.87716L20.1505 5.75404C20.1631 5.74941 20.1714 5.73744 20.1714 5.72406C20.1714 5.71068 20.1631 5.69871 20.1505 5.69408L19.8166 5.57092C19.5463 5.47124 19.3333 5.25819 19.2336 4.98791L19.1105 4.65401C19.1058 4.64146 19.0939 4.63312 19.0805 4.63312C19.0671 4.63312 19.0551 4.64146 19.0505 4.65401L18.9274 4.98791C18.8277 5.25819 18.6146 5.47126 18.3444 5.57092L18.0105 5.69408C17.9979 5.69871 17.9896 5.71068 17.9896 5.72406C17.9896 5.73744 17.9979 5.74941 18.0105 5.75404Z"
                fill={`url(#${gradientId2})`}
            />
            <path
                d="M18.0105 18.6984L18.3443 18.8215C18.6146 18.9212 18.8277 19.1342 18.9274 19.4045L19.0505 19.7384C19.0551 19.7509 19.0671 19.7593 19.0805 19.7593C19.0939 19.7593 19.1058 19.7509 19.1105 19.7384L19.2336 19.4045C19.3333 19.1342 19.5463 18.9212 19.8166 18.8215L20.1505 18.6984C20.1631 18.6937 20.1714 18.6818 20.1714 18.6684C20.1714 18.655 20.1631 18.643 20.1505 18.6384L19.8166 18.5153C19.5463 18.4156 19.3333 18.2025 19.2336 17.9322L19.1105 17.5983C19.1058 17.5858 19.0939 17.5775 19.0805 17.5775C19.0671 17.5775 19.0551 17.5858 19.0505 17.5983L18.9274 17.9322C18.8277 18.2025 18.6146 18.4156 18.3444 18.5153L18.0105 18.6384C17.9979 18.643 17.9896 18.655 17.9896 18.6684C17.9896 18.6818 17.9979 18.6937 18.0105 18.6984Z"
                fill={`url(#${gradientId2})`}
            />
            <g clipPath="url(#clip0_18170_61246)">
                <path
                    d="M10.0001 11.9999L11.3335 13.3332L14.0001 10.6666M6.5668 9.74657C6.46949 9.30825 6.48443 8.85246 6.61023 8.42146C6.73604 7.99046 6.96863 7.5982 7.28644 7.28105C7.60425 6.9639 7.997 6.73213 8.42827 6.60723C8.85953 6.48233 9.31535 6.46835 9.75346 6.56657C9.9946 6.18944 10.3268 5.87907 10.7194 5.66409C11.1121 5.44911 11.5525 5.33643 12.0001 5.33643C12.4478 5.33643 12.8882 5.44911 13.2808 5.66409C13.6735 5.87907 14.0057 6.18944 14.2468 6.56657C14.6856 6.46792 15.1422 6.48184 15.5741 6.60704C16.0061 6.73225 16.3994 6.96466 16.7174 7.28267C17.0354 7.60068 17.2678 7.99395 17.393 8.4259C17.5182 8.85786 17.5321 9.31446 17.4335 9.75324C17.8106 9.99437 18.121 10.3266 18.3359 10.7192C18.5509 11.1118 18.6636 11.5523 18.6636 11.9999C18.6636 12.4475 18.5509 12.888 18.3359 13.2806C18.121 13.6732 17.8106 14.0054 17.4335 14.2466C17.5317 14.6847 17.5177 15.1405 17.3928 15.5718C17.2679 16.003 17.0361 16.3958 16.719 16.7136C16.4018 17.0314 16.0096 17.264 15.5786 17.3898C15.1476 17.5156 14.6918 17.5305 14.2535 17.4332C14.0126 17.8118 13.6802 18.1235 13.2869 18.3394C12.8936 18.5554 12.4521 18.6686 12.0035 18.6686C11.5548 18.6686 11.1133 18.5554 10.72 18.3394C10.3267 18.1235 9.99429 17.8118 9.75346 17.4332C9.31535 17.5315 8.85953 17.5175 8.42827 17.3926C7.997 17.2677 7.60425 17.0359 7.28644 16.7188C6.96863 16.4016 6.73604 16.0093 6.61023 15.5783C6.48443 15.1473 6.46949 14.6916 6.5668 14.2532C6.18677 14.0127 5.87374 13.68 5.65683 13.286C5.43992 12.8921 5.32617 12.4496 5.32617 11.9999C5.32617 11.5502 5.43992 11.1077 5.65683 10.7138C5.87374 10.3198 6.18677 9.98707 6.5668 9.74657Z"
                    stroke={`url(#${gradientId3})`}
                    strokeWidth="1.83"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </g>
        </svg>
    ) : (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <path
                d="M2.04351 4.60472L2.73905 4.86123C3.30217 5.06889 3.74607 5.51276 3.95369 6.07584L4.21023 6.77143C4.21988 6.79757 4.24481 6.81494 4.27268 6.81494C4.30056 6.81494 4.32549 6.79757 4.33513 6.77143L4.59168 6.07588C4.79934 5.51276 5.24324 5.06889 5.80631 4.86123L6.50189 4.60472C6.52804 4.59508 6.54541 4.57015 6.54541 4.54227C6.54541 4.5144 6.52804 4.48947 6.50189 4.47982L5.80627 4.22323C5.2432 4.01557 4.79934 3.57171 4.59168 3.00863L4.33513 2.313C4.32549 2.28685 4.30056 2.26949 4.27268 2.26949C4.24481 2.26949 4.21988 2.28685 4.21023 2.313L3.95369 3.00863C3.74603 3.57171 3.30217 4.01561 2.73909 4.22323L2.04347 4.47982C2.01732 4.48947 1.99996 4.5144 1.99996 4.54227C1.99996 4.57015 2.01736 4.59508 2.04351 4.60472Z"
                fill="white"
            />
            <path
                d="M18.0105 5.75404L18.3443 5.87716C18.6146 5.97684 18.8277 6.18989 18.9274 6.46017L19.0505 6.79405C19.0551 6.80661 19.0671 6.81494 19.0805 6.81494C19.0939 6.81494 19.1058 6.80661 19.1105 6.79405L19.2336 6.46019C19.3333 6.18989 19.5463 5.97684 19.8166 5.87716L20.1505 5.75404C20.1631 5.74941 20.1714 5.73744 20.1714 5.72406C20.1714 5.71068 20.1631 5.69871 20.1505 5.69408L19.8166 5.57092C19.5463 5.47124 19.3333 5.25819 19.2336 4.98791L19.1105 4.65401C19.1058 4.64146 19.0939 4.63312 19.0805 4.63312C19.0671 4.63312 19.0551 4.64146 19.0505 4.65401L18.9274 4.98791C18.8277 5.25819 18.6146 5.47126 18.3444 5.57092L18.0105 5.69408C17.9979 5.69871 17.9896 5.71068 17.9896 5.72406C17.9896 5.73744 17.9979 5.74941 18.0105 5.75404Z"
                fill="white"
            />
            <path
                d="M18.0105 18.6984L18.3443 18.8215C18.6146 18.9212 18.8277 19.1342 18.9274 19.4045L19.0505 19.7384C19.0551 19.7509 19.0671 19.7593 19.0805 19.7593C19.0939 19.7593 19.1058 19.7509 19.1105 19.7384L19.2336 19.4045C19.3333 19.1342 19.5463 18.9212 19.8166 18.8215L20.1505 18.6984C20.1631 18.6937 20.1714 18.6818 20.1714 18.6684C20.1714 18.655 20.1631 18.643 20.1505 18.6384L19.8166 18.5153C19.5463 18.4156 19.3333 18.2025 19.2336 17.9322L19.1105 17.5983C19.1058 17.5858 19.0939 17.5775 19.0805 17.5775C19.0671 17.5775 19.0551 17.5858 19.0505 17.5983L18.9274 17.9322C18.8277 18.2025 18.6146 18.4156 18.3444 18.5153L18.0105 18.6384C17.9979 18.643 17.9896 18.655 17.9896 18.6684C17.9896 18.6818 17.9979 18.6937 18.0105 18.6984Z"
                fill="white"
            />
            <g clipPath="url(#clip0_18273_41505)">
                <path
                    d="M10.0001 11.9999L11.3335 13.3332L14.0001 10.6666M6.5668 9.74657C6.46949 9.30825 6.48443 8.85246 6.61023 8.42146C6.73604 7.99046 6.96863 7.5982 7.28644 7.28105C7.60425 6.9639 7.997 6.73213 8.42827 6.60723C8.85953 6.48233 9.31535 6.46835 9.75346 6.56657C9.9946 6.18944 10.3268 5.87907 10.7194 5.66409C11.1121 5.44911 11.5525 5.33643 12.0001 5.33643C12.4478 5.33643 12.8882 5.44911 13.2808 5.66409C13.6735 5.87907 14.0057 6.18944 14.2468 6.56657C14.6856 6.46792 15.1422 6.48184 15.5741 6.60704C16.0061 6.73225 16.3994 6.96466 16.7174 7.28267C17.0354 7.60068 17.2678 7.99395 17.393 8.4259C17.5182 8.85786 17.5321 9.31446 17.4335 9.75324C17.8106 9.99437 18.121 10.3266 18.3359 10.7192C18.5509 11.1118 18.6636 11.5523 18.6636 11.9999C18.6636 12.4475 18.5509 12.888 18.3359 13.2806C18.121 13.6732 17.8106 14.0054 17.4335 14.2466C17.5317 14.6847 17.5177 15.1405 17.3928 15.5718C17.2679 16.003 17.0361 16.3958 16.719 16.7136C16.4018 17.0314 16.0096 17.264 15.5786 17.3898C15.1476 17.5156 14.6918 17.5305 14.2535 17.4332C14.0126 17.8118 13.6802 18.1235 13.2869 18.3394C12.8936 18.5554 12.4521 18.6686 12.0035 18.6686C11.5548 18.6686 11.1133 18.5554 10.72 18.3394C10.3267 18.1235 9.99429 17.8118 9.75346 17.4332C9.31535 17.5315 8.85953 17.5175 8.42827 17.3926C7.997 17.2677 7.60425 17.0359 7.28644 16.7188C6.96863 16.4016 6.73604 16.0093 6.61023 15.5783C6.48443 15.1473 6.46949 14.6916 6.5668 14.2532C6.18677 14.0127 5.87374 13.68 5.65683 13.286C5.43992 12.8921 5.32617 12.4496 5.32617 11.9999C5.32617 11.5502 5.43992 11.1077 5.65683 10.7138C5.87374 10.3198 6.18677 9.98707 6.5668 9.74657Z"
                    stroke="white"
                    strokeWidth="1.83"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </g>
            <defs>
                <clipPath id="clip0_18273_41505">
                    <rect
                        width="16"
                        height="16"
                        fill="white"
                        transform="translate(4 4)"
                    />
                </clipPath>
            </defs>
        </svg>
    );
};

export const SmartScribe = ({
    className,
    gradient = false,
}: {
    className?: string;
    gradient?: boolean;
}) => {
    const { primaryFrom, primaryTo } = getGradientColours();

    return gradient ? (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <g clipPath="url(#clip0_14658_103021)">
                <path
                    d="M5.20145 15.478L16.3231 4.35383C16.7637 3.91335 17.3612 3.66594 17.9842 3.66602C18.6073 3.66609 19.2047 3.91366 19.6452 4.35425C20.0857 4.79484 20.3331 5.39237 20.333 6.01538C20.3329 6.63839 20.0854 7.23585 19.6448 7.67633L8.52312 18.7997M5.20145 15.478C5.00797 15.6709 4.86488 15.9084 4.78478 16.1697L3.68395 19.7963C3.66241 19.8684 3.66079 19.945 3.67924 20.0179C3.6977 20.0908 3.73555 20.1574 3.78878 20.2105C3.84201 20.2636 3.90863 20.3014 3.98158 20.3197C4.05453 20.3381 4.13108 20.3363 4.20312 20.3147L7.83062 19.2147C8.09159 19.1353 8.32909 18.9931 8.52228 18.8005L8.52312 18.7997M5.20145 15.478L8.52312 18.7997"
                    stroke="url(#paint0_linear_14658_103021)"
                    strokeWidth="1.83333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M13.8482 16.6858L14.5437 16.9423C15.1069 17.1499 15.5508 17.5938 15.7584 18.1569L16.0149 18.8525C16.0246 18.8786 16.0495 18.896 16.0774 18.896C16.1052 18.896 16.1302 18.8786 16.1398 18.8525L16.3964 18.1569C16.604 17.5938 17.0479 17.1499 17.611 16.9423L18.3066 16.6858C18.3327 16.6761 18.3501 16.6512 18.3501 16.6233C18.3501 16.5955 18.3327 16.5705 18.3066 16.5609L17.611 16.3043C17.0479 16.0966 16.604 15.6528 16.3964 15.0897L16.1398 14.3941C16.1302 14.3679 16.1052 14.3505 16.0774 14.3505C16.0495 14.3505 16.0246 14.3679 16.0149 14.3941L15.7584 15.0897C15.5507 15.6528 15.1069 16.0967 14.5438 16.3043L13.8482 16.5609C13.822 16.5705 13.8046 16.5955 13.8046 16.6233C13.8046 16.6512 13.822 16.6761 13.8482 16.6858Z"
                    fill="url(#paint1_linear_14658_103021)"
                />
                <path
                    d="M3.20367 9.78978L3.89921 10.0463C4.46233 10.254 4.90622 10.6978 5.11384 11.2609L5.37039 11.9565C5.38003 11.9826 5.40497 12 5.43284 12C5.46071 12 5.48565 11.9826 5.49529 11.9565L5.75184 11.2609C5.95949 10.6978 6.40339 10.254 6.96647 10.0463L7.66205 9.78978C7.6882 9.78014 7.70557 9.7552 7.70557 9.72733C7.70557 9.69946 7.6882 9.67452 7.66205 9.66488L6.96643 9.40829C6.40335 9.20063 5.9595 8.75677 5.75184 8.19369L5.49529 7.49806C5.48565 7.47191 5.46071 7.45455 5.43284 7.45455C5.40497 7.45455 5.38003 7.47191 5.37039 7.49806L5.11384 8.19369C4.90618 8.75677 4.46232 9.20067 3.89925 9.40829L3.20363 9.66488C3.17748 9.67452 3.16011 9.69946 3.16011 9.72733C3.16011 9.7552 3.17752 9.78014 3.20367 9.78978Z"
                    fill="url(#paint2_linear_14658_103021)"
                />
                <path
                    d="M9.08469 5.5641L9.41855 5.68722C9.68885 5.7869 9.90192 5.99995 10.0016 6.27023L10.1247 6.60411C10.1293 6.61666 10.1413 6.625 10.1547 6.625C10.1681 6.625 10.18 6.61666 10.1847 6.60411L10.3078 6.27025C10.4075 5.99995 10.6206 5.7869 10.8908 5.68722L11.2247 5.5641C11.2373 5.55947 11.2456 5.5475 11.2456 5.53412C11.2456 5.52074 11.2373 5.50877 11.2247 5.50414L10.8908 5.38098C10.6205 5.2813 10.4075 5.06825 10.3078 4.79797L10.1847 4.46407C10.18 4.45152 10.1681 4.44318 10.1547 4.44318C10.1413 4.44318 10.1293 4.45152 10.1247 4.46407L10.0016 4.79797C9.9019 5.06825 9.68885 5.28132 9.41857 5.38098L9.08467 5.50414C9.07212 5.50877 9.06379 5.52074 9.06379 5.53412C9.06379 5.5475 9.07214 5.55947 9.08469 5.5641Z"
                    fill="url(#paint3_linear_14658_103021)"
                />
            </g>
            <defs>
                <linearGradient
                    id="paint0_linear_14658_103021"
                    x1="3.6665"
                    y1="11.9992"
                    x2="20.333"
                    y2="11.9992"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
                <linearGradient
                    id="paint1_linear_14658_103021"
                    x1="18.3501"
                    y1="16.6233"
                    x2="13.8046"
                    y2="16.6233"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
                <linearGradient
                    id="paint2_linear_14658_103021"
                    x1="7.70557"
                    y1="9.72727"
                    x2="3.16011"
                    y2="9.72727"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
                <linearGradient
                    id="paint3_linear_14658_103021"
                    x1="11.2456"
                    y1="5.53409"
                    x2="9.06379"
                    y2="5.53409"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
                <clipPath id="clip0_14658_103021">
                    <rect
                        width="20"
                        height="20"
                        fill="white"
                        transform="translate(2 2)"
                    />
                </clipPath>
            </defs>
        </svg>
    ) : (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <g clipPath="url(#clip0_14658_103021)">
                <path
                    d="M5.20145 15.478L16.3231 4.35383C16.7637 3.91335 17.3612 3.66594 17.9842 3.66602C18.6073 3.66609 19.2047 3.91366 19.6452 4.35425C20.0857 4.79484 20.3331 5.39237 20.333 6.01538C20.3329 6.63839 20.0854 7.23585 19.6448 7.67633L8.52312 18.7997M5.20145 15.478C5.00797 15.6709 4.86488 15.9084 4.78478 16.1697L3.68395 19.7963C3.66241 19.8684 3.66079 19.945 3.67924 20.0179C3.6977 20.0908 3.73555 20.1574 3.78878 20.2105C3.84201 20.2636 3.90863 20.3014 3.98158 20.3197C4.05453 20.3381 4.13108 20.3363 4.20312 20.3147L7.83062 19.2147C8.09159 19.1353 8.32909 18.9931 8.52228 18.8005L8.52312 18.7997M5.20145 15.478L8.52312 18.7997"
                    stroke="white"
                    strokeWidth="1.83333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M13.8482 16.6858L14.5437 16.9423C15.1069 17.1499 15.5508 17.5938 15.7584 18.1569L16.0149 18.8525C16.0246 18.8786 16.0495 18.896 16.0774 18.896C16.1052 18.896 16.1302 18.8786 16.1398 18.8525L16.3964 18.1569C16.604 17.5938 17.0479 17.1499 17.611 16.9423L18.3066 16.6858C18.3327 16.6761 18.3501 16.6512 18.3501 16.6233C18.3501 16.5955 18.3327 16.5705 18.3066 16.5609L17.611 16.3043C17.0479 16.0966 16.604 15.6528 16.3964 15.0897L16.1398 14.3941C16.1302 14.3679 16.1052 14.3505 16.0774 14.3505C16.0495 14.3505 16.0246 14.3679 16.0149 14.3941L15.7584 15.0897C15.5507 15.6528 15.1069 16.0967 14.5438 16.3043L13.8482 16.5609C13.822 16.5705 13.8046 16.5955 13.8046 16.6233C13.8046 16.6512 13.822 16.6761 13.8482 16.6858Z"
                    fill="white"
                />
                <path
                    d="M3.20367 9.78978L3.89921 10.0463C4.46233 10.254 4.90622 10.6978 5.11384 11.2609L5.37039 11.9565C5.38003 11.9826 5.40497 12 5.43284 12C5.46071 12 5.48565 11.9826 5.49529 11.9565L5.75184 11.2609C5.95949 10.6978 6.40339 10.254 6.96647 10.0463L7.66205 9.78978C7.6882 9.78014 7.70557 9.75521 7.70557 9.72733C7.70557 9.69946 7.6882 9.67452 7.66205 9.66488L6.96643 9.40829C6.40335 9.20063 5.9595 8.75677 5.75184 8.19369L5.49529 7.49806C5.48565 7.47191 5.46071 7.45455 5.43284 7.45455C5.40497 7.45455 5.38003 7.47191 5.37039 7.49806L5.11384 8.19369C4.90618 8.75677 4.46232 9.20067 3.89925 9.40829L3.20363 9.66488C3.17748 9.67452 3.16011 9.69946 3.16011 9.72733C3.16011 9.7552 3.17752 9.78014 3.20367 9.78978Z"
                    fill="white"
                />
                <path
                    d="M9.08469 5.5641L9.41855 5.68722C9.68885 5.7869 9.90192 5.99995 10.0016 6.27023L10.1247 6.60411C10.1293 6.61666 10.1413 6.625 10.1547 6.625C10.1681 6.625 10.18 6.61666 10.1847 6.60411L10.3078 6.27025C10.4075 5.99995 10.6206 5.7869 10.8908 5.68722L11.2247 5.5641C11.2373 5.55947 11.2456 5.5475 11.2456 5.53412C11.2456 5.52074 11.2373 5.50877 11.2247 5.50414L10.8908 5.38098C10.6205 5.2813 10.4075 5.06825 10.3078 4.79797L10.1847 4.46407C10.18 4.45152 10.1681 4.44318 10.1547 4.44318C10.1413 4.44318 10.1293 4.45152 10.1247 4.46407L10.0016 4.79797C9.9019 5.06825 9.68885 5.28132 9.41857 5.38098L9.08467 5.50414C9.07212 5.50877 9.06379 5.52074 9.06379 5.53412C9.06379 5.5475 9.07214 5.55947 9.08469 5.5641Z"
                    fill="white"
                />
            </g>
            <defs>
                <clipPath id="clip0_14658_103021">
                    <rect
                        width="20"
                        height="20"
                        fill="white"
                        transform="translate(2 2)"
                    />
                </clipPath>
            </defs>
        </svg>
    );
};

export const GradientFilesIcon = ({ className }: WithClassName) => {
    const { primaryFrom, primaryTo } = getGradientColours();

    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <path
                d="M10.3337 1.33398H5.73366C5.46699 1.33398 5.20033 1.46732 5.00033 1.66732C4.80033 1.86732 4.66699 2.13398 4.66699 2.40065V10.934C4.66699 11.2007 4.80033 11.4673 5.00033 11.6673C5.20033 11.8673 5.46699 12.0007 5.73366 12.0007H12.267C12.5337 12.0007 12.8003 11.8673 13.0003 11.6673C13.2003 11.4673 13.3337 11.2007 13.3337 10.934V4.33398L10.3337 1.33398Z"
                stroke="url(#paint0_linear_14729_11685)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M2 5.06641V13.5997C2 13.8664 2.13333 14.1331 2.33333 14.3331C2.53333 14.5331 2.8 14.6664 3.06667 14.6664H9.6"
                stroke="url(#paint1_linear_14729_11685)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M10 1.33398V4.66732H13.3333"
                stroke="url(#paint2_linear_14729_11685)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <defs>
                <linearGradient
                    id="paint0_linear_14729_11685"
                    x1="4.66699"
                    y1="6.66732"
                    x2="13.3337"
                    y2="6.66732"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
                <linearGradient
                    id="paint1_linear_14729_11685"
                    x1="2"
                    y1="9.86641"
                    x2="9.6"
                    y2="9.86641"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
                <linearGradient
                    id="paint2_linear_14729_11685"
                    x1="10"
                    y1="3.00065"
                    x2="13.3333"
                    y2="3.00065"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
            </defs>
        </svg>
    );
};

export const GradientDownloadIcon = ({ className }: WithClassName) => {
    const { primaryFrom, primaryTo } = getGradientColours();

    return (
        <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <path
                d="M42 30V38C42 39.0609 41.5786 40.0783 40.8284 40.8284C40.0783 41.5786 39.0609 42 38 42H10C8.93913 42 7.92172 41.5786 7.17157 40.8284C6.42143 40.0783 6 39.0609 6 38V30"
                stroke="url(#paint0_linear_14729_25998)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M14 20L24 30L34 20"
                stroke="url(#paint1_linear_14729_25998)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M24 30V6"
                stroke="url(#paint2_linear_14729_25998)"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <defs>
                <linearGradient
                    id="paint0_linear_14729_25998"
                    x1="6"
                    y1="36"
                    x2="42"
                    y2="36"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
                <linearGradient
                    id="paint1_linear_14729_25998"
                    x1="14"
                    y1="25"
                    x2="34"
                    y2="25"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
                <linearGradient
                    id="paint2_linear_14729_25998"
                    x1="24"
                    y1="18"
                    x2="25"
                    y2="18"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
            </defs>
        </svg>
    );
};

export const CreditsIcon = ({ className }: WithClassName) => {
    const { primaryFrom, primaryTo } = getGradientColours();
    // Linear gradient IDs must be unique in every instance
    const uniqueId = `credits-gradient-${uuidv4()}`;

    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <path
                d="M4.12803 12.0007L12 4.12872L19.872 12.0007L12 19.8727L4.12803 12.0007Z"
                stroke={`url(#${uniqueId})`}
                strokeWidth="3.26735"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <defs>
                <linearGradient
                    id={uniqueId}
                    x1="6.90883"
                    y1="6.90953"
                    x2="17.0912"
                    y2="17.0919"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
            </defs>
        </svg>
    );
};

export const CheckIcon = ({ className }: WithClassName) => (
    <svg
        aria-hidden="true"
        className={clsx(
            "h-6 w-6 flex-none fill-current stroke-current",
            className,
        )}
    >
        <path
            d="M9.307 12.248a.75.75 0 1 0-1.114 1.004l1.114-1.004ZM11 15.25l-.557.502a.75.75 0 0 0 1.15-.043L11 15.25Zm4.844-5.041a.75.75 0 0 0-1.188-.918l1.188.918Zm-7.651 3.043 2.25 2.5 1.114-1.004-2.25-2.5-1.114 1.004Zm3.4 2.457 4.25-5.5-1.187-.918-4.25 5.5 1.188.918Z"
            strokeWidth={0}
        />
        <circle
            cx={12}
            cy={12}
            r={8.25}
            fill="none"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export const ColumnsIcon = ({ className }: WithClassName) => {
    const { primaryTo } = getGradientColours();

    return (
        <svg
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <path
                d="M16.3333 2.5H4.66667C3.74619 2.5 3 3.24619 3 4.16667V15.8333C3 16.7538 3.74619 17.5 4.66667 17.5H16.3333C17.2538 17.5 18 16.7538 18 15.8333V4.16667C18 3.24619 17.2538 2.5 16.3333 2.5Z"
                stroke={primaryTo}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M10.5 2.5V17.5"
                stroke={primaryTo}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};

export const DefaultAvatarIcon = () => (
    <svg
        className="h-full w-full text-gray-300"
        fill="currentColor"
        viewBox="0 0 24 24"
    >
        <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
);

export const Google = ({ className }: WithClassName) => (
    <svg
        width="40"
        height="40"
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
    >
        <path
            d="M29.6 20.2273C29.6 19.5182 29.5364 18.8364 29.4182 18.1818H20V22.05H25.3818C25.15 23.3 24.4455 24.3591 23.3864 25.0682V27.5773H26.6182C28.5091 25.8364 29.6 23.2727 29.6 20.2273Z"
            fill="#4285F4"
        />
        <path
            d="M20 30C22.7 30 24.9636 29.1045 26.6181 27.5773L23.3863 25.0682C22.4909 25.6682 21.3454 26.0227 20 26.0227C17.3954 26.0227 15.1909 24.2636 14.4045 21.9H11.0636V24.4909C12.7091 27.7591 16.0909 30 20 30Z"
            fill="#34A853"
        />
        <path
            d="M14.4045 21.9C14.2045 21.3 14.0909 20.6591 14.0909 20C14.0909 19.3409 14.2045 18.7 14.4045 18.1V15.5091H11.0636C10.3864 16.8591 10 18.3864 10 20C10 21.6136 10.3864 23.1409 11.0636 24.4909L14.4045 21.9Z"
            fill="#FBBC04"
        />
        <path
            d="M20 13.9773C21.4681 13.9773 22.7863 14.4818 23.8227 15.4727L26.6909 12.6045C24.9591 10.9909 22.6954 10 20 10C16.0909 10 12.7091 12.2409 11.0636 15.5091L14.4045 18.1C15.1909 15.7364 17.3954 13.9773 20 13.9773Z"
            fill="#E94235"
        />
    </svg>
);

export const LinkedIn = ({ className }: WithClassName) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        height="800"
        width="1200"
        viewBox="-11.493675 -16.3118 99.61185 97.8708"
        className={className}
    >
        <path
            fill="#0a66c2"
            d="M72.865 61.1094a1.2 1.2 0 001.21-1.269c0-.9-.543-1.33-1.657-1.33h-1.8v4.712h.677v-2.054h.832l.019.025 1.291 2.029h.724l-1.389-2.1zm-.783-.472h-.785v-1.593h.995c.514 0 1.1.084 1.1.757 0 .774-.593.836-1.314.836m-16.873-5.433h-9.6v-15.034c0-3.585-.064-8.2-4.993-8.2-5 0-5.765 3.906-5.765 7.939v15.294h-9.6v-30.916h9.216v4.225h.129a10.1 10.1 0 019.093-4.994c9.73 0 11.524 6.4 11.524 14.726zm-40.79-35.143a5.571 5.571 0 115.57-5.572 5.571 5.571 0 01-5.57 5.572m4.8 35.143h-9.61v-30.917h9.61zm40.776-55.2H4.781A4.728 4.728 0 000 4.6744v55.439a4.731 4.731 0 004.781 4.675h55.21a4.741 4.741 0 004.8-4.675V4.6704a4.738 4.738 0 00-4.8-4.67"
        />
        <path
            fill="#0a66c2"
            d="M72.164 56.4114a4.418 4.418 0 10.085 0h-.085m0 8.33a3.874 3.874 0 113.809-3.938v.065a3.791 3.791 0 01-3.708 3.871h-.1"
        />
    </svg>
);

export const InlineLoader = () => {
    return (
        <div role="status">
            <svg
                aria-hidden="true"
                className="h-6 w-6 animate-spin fill-primary-900 text-neutral-200"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                />
                <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                />
            </svg>
            <span className="sr-only">Loading...</span>
        </div>
    );
};

export const Loader = () => {
    return (
        <div
            role="status"
            className="absolute left-1/2 top-2/4 -translate-x-1/2 -translate-y-1/2"
        >
            <svg
                aria-hidden="true"
                className="mr-2 h-8 w-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                />
                <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                />
            </svg>
            <span className="sr-only">Loading...</span>
        </div>
    );
};

export const OneLogo = ({
    className,
    height = 152,
    width = 152,
    variant = "dark",
}: {
    className?: string;
    height?: number;
    width?: number;
    variant?: "dark" | "light";
}) => (
    <svg
        width={width}
        height={height}
        viewBox="0 0 152 152"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
    >
        <rect
            width="152"
            height="152"
            fill={variant === "dark" ? "white" : "#1C0B3E"}
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8 0H144C148.418 0 152 3.58172 152 8V143.941C152 148.36 148.418 151.941 144 151.941H8C3.58172 151.941 0 148.36 0 143.941V8C0 3.58172 3.58172 0 8 0ZM51.8325 58.7154C61.014 58.7154 67.7921 56.5035 71.8869 53.0586V53.0713V103.436H46.8984V121.881H114.59V103.436H91.0129V25.3086H76.1216C71.8869 35.2493 65.1088 40.6264 50.9932 40.6264H47.3181V58.7154H51.8325Z"
            fill={variant === "dark" ? "#1C0B3E" : "white"}
        />
    </svg>
);

export const Logo = ({ className }: WithClassName) => (
    <img
        src={LogoImage}
        alt="Logo"
        role="presentation"
        className={className}
    />
);

export const PaintBrushIcon = ({ className, pathClassName }: WithClassName) => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
    >
        <path
            d="M15.3077 2.1916L11.666 5.83326L10.341 4.50826C10.0287 4.19784 9.60633 4.02361 9.16602 4.02361C8.72571 4.02361 8.30329 4.19784 7.99102 4.50826L6.66602 5.83326L14.166 13.3333L15.491 12.0083C15.8014 11.696 15.9757 11.2736 15.9757 10.8333C15.9757 10.393 15.8014 9.97053 15.491 9.65826L14.166 8.33326L17.8077 4.6916C18.1392 4.36007 18.3254 3.91044 18.3254 3.4416C18.3254 2.97275 18.1392 2.52312 17.8077 2.1916C17.4762 1.86007 17.0265 1.67383 16.5577 1.67383C16.0888 1.67383 15.6392 1.86007 15.3077 2.1916Z"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={pathClassName}
        />
        <path
            d="M7.49935 6.66699C5.83268 9.16699 4.16602 9.58366 1.66602 10.0003L8.33268 18.3337C9.99935 17.5003 13.3327 14.167 13.3327 12.5003"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={pathClassName}
        />
        <path
            d="M12.0833 14.5833L3.75 12.5"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={pathClassName}
        />
    </svg>
);

export const PencilLineIcon = ({ className }: WithClassName) => (
    <svg
        width="24"
        height="25"
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
    >
        <mask
            id="mask0_1268_4896"
            style={{ maskType: "alpha" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="24"
            height="25"
        >
            <rect
                y="0.5"
                width="24"
                height="24"
                fill="#D9D9D9"
            />
        </mask>
        <g mask="url(#mask0_1268_4896)">
            <path
                d="M4 24.5C3.45 24.5 2.97917 24.3042 2.5875 23.9125C2.19583 23.5208 2 23.05 2 22.5C2 21.95 2.19583 21.4792 2.5875 21.0875C2.97917 20.6958 3.45 20.5 4 20.5H20C20.55 20.5 21.0208 20.6958 21.4125 21.0875C21.8042 21.4792 22 21.95 22 22.5C22 23.05 21.8042 23.5208 21.4125 23.9125C21.0208 24.3042 20.55 24.5 20 24.5H4ZM4 17.5V14.675C4 14.5417 4.025 14.4125 4.075 14.2875C4.125 14.1625 4.2 14.05 4.3 13.95L15.2 3.075C15.3833 2.89167 15.5958 2.75 15.8375 2.65C16.0792 2.55 16.3333 2.5 16.6 2.5C16.8667 2.5 17.125 2.55 17.375 2.65C17.625 2.75 17.85 2.9 18.05 3.1L19.425 4.5C19.625 4.68333 19.7708 4.9 19.8625 5.15C19.9542 5.4 20 5.65833 20 5.925C20 6.175 19.9542 6.42083 19.8625 6.6625C19.7708 6.90417 19.625 7.125 19.425 7.325L8.55 18.2C8.45 18.3 8.3375 18.375 8.2125 18.425C8.0875 18.475 7.95833 18.5 7.825 18.5H5C4.71667 18.5 4.47917 18.4042 4.2875 18.2125C4.09583 18.0208 4 17.7833 4 17.5ZM16.6 7.3L18 5.9L16.6 4.5L15.2 5.9L16.6 7.3Z"
                fill="white"
            />
        </g>
    </svg>
);

export const QuoteIcon = ({ className }: WithClassName) => (
    <svg
        aria-hidden="true"
        width={105}
        height={78}
        className={className}
    >
        <path d="M25.086 77.292c-4.821 0-9.115-1.205-12.882-3.616-3.767-2.561-6.78-6.102-9.04-10.622C1.054 58.534 0 53.411 0 47.686c0-5.273.904-10.396 2.712-15.368 1.959-4.972 4.746-9.567 8.362-13.786a59.042 59.042 0 0 1 12.43-11.3C28.325 3.917 33.599 1.507 39.324 0l11.074 13.786c-6.479 2.561-11.677 5.951-15.594 10.17-3.767 4.219-5.65 7.835-5.65 10.848 0 1.356.377 2.863 1.13 4.52.904 1.507 2.637 3.089 5.198 4.746 3.767 2.41 6.328 4.972 7.684 7.684 1.507 2.561 2.26 5.5 2.26 8.814 0 5.123-1.959 9.19-5.876 12.204-3.767 3.013-8.588 4.52-14.464 4.52Zm54.24 0c-4.821 0-9.115-1.205-12.882-3.616-3.767-2.561-6.78-6.102-9.04-10.622-2.11-4.52-3.164-9.643-3.164-15.368 0-5.273.904-10.396 2.712-15.368 1.959-4.972 4.746-9.567 8.362-13.786a59.042 59.042 0 0 1 12.43-11.3C82.565 3.917 87.839 1.507 93.564 0l11.074 13.786c-6.479 2.561-11.677 5.951-15.594 10.17-3.767 4.219-5.65 7.835-5.65 10.848 0 1.356.377 2.863 1.13 4.52.904 1.507 2.637 3.089 5.198 4.746 3.767 2.41 6.328 4.972 7.684 7.684 1.507 2.561 2.26 5.5 2.26 8.814 0 5.123-1.959 9.19-5.876 12.204-3.767 3.013-8.588 4.52-14.464 4.52Z" />
    </svg>
);

export const SquareIcon = ({ className }: WithClassName) => {
    const { primaryTo } = getGradientColours();

    return (
        <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <path
                d="M14.8333 1.5H3.16667C2.24619 1.5 1.5 2.24619 1.5 3.16667V14.8333C1.5 15.7538 2.24619 16.5 3.16667 16.5H14.8333C15.7538 16.5 16.5 15.7538 16.5 14.8333V3.16667C16.5 2.24619 15.7538 1.5 14.8333 1.5Z"
                stroke={primaryTo}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};

export const StarsIcon = ({ className }: WithClassName) => (
    <svg
        width="21"
        height="20"
        viewBox="0 0 21 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
    >
        <mask
            id="path-1-inside-1_1675_1055"
            fill="white"
        >
            <path d="M20.3707 7.1612L18.3097 6.3301C16.6412 5.65727 15.3259 4.21914 14.7107 2.39473L13.9505 0.140994C13.922 0.0562705 13.8481 0 13.7655 0C13.6829 0 13.609 0.0562705 13.5804 0.140994L12.8203 2.3946C12.205 4.21914 10.8897 5.65727 9.22124 6.3301L7.16019 7.1612C7.08271 7.19245 7.03125 7.27324 7.03125 7.36355C7.03125 7.45386 7.08271 7.53465 7.16019 7.56589L9.22136 8.39725C10.8898 9.07008 12.205 10.5082 12.8203 12.3326L13.5804 14.5865C13.609 14.6712 13.6829 14.7275 13.7655 14.7275C13.8481 14.7275 13.922 14.6712 13.9505 14.5865L14.7107 12.3326C15.326 10.5082 16.6412 9.06995 18.3096 8.39725L20.3708 7.56589C20.4483 7.53465 20.4997 7.45386 20.4997 7.36355C20.4997 7.27324 20.4481 7.19245 20.3707 7.1612Z" />
        </mask>
        <path
            d="M20.3707 7.1612L18.3097 6.3301C16.6412 5.65727 15.3259 4.21914 14.7107 2.39473L13.9505 0.140994C13.922 0.0562705 13.8481 0 13.7655 0C13.6829 0 13.609 0.0562705 13.5804 0.140994L12.8203 2.3946C12.205 4.21914 10.8897 5.65727 9.22124 6.3301L7.16019 7.1612C7.08271 7.19245 7.03125 7.27324 7.03125 7.36355C7.03125 7.45386 7.08271 7.53465 7.16019 7.56589L9.22136 8.39725C10.8898 9.07008 12.205 10.5082 12.8203 12.3326L13.5804 14.5865C13.609 14.6712 13.6829 14.7275 13.7655 14.7275C13.8481 14.7275 13.922 14.6712 13.9505 14.5865L14.7107 12.3326C15.326 10.5082 16.6412 9.06995 18.3096 8.39725L20.3708 7.56589C20.4483 7.53465 20.4997 7.45386 20.4997 7.36355C20.4997 7.27324 20.4481 7.19245 20.3707 7.1612Z"
            fill="white"
        />
        <path
            d="M20.3707 7.1612L21.3789 4.66133L21.3788 4.6613L20.3707 7.1612ZM18.3097 6.3301L19.3178 3.8302L19.3178 3.83018L18.3097 6.3301ZM14.7107 2.39473L17.2649 1.53345L17.2648 1.53324L14.7107 2.39473ZM13.9505 0.140994L11.3964 1.00247L11.3964 1.00248L13.9505 0.140994ZM13.5804 0.140994L16.1346 1.00252L16.1346 1.00247L13.5804 0.140994ZM12.8203 2.3946L10.2661 1.53308L10.2661 1.53323L12.8203 2.3946ZM9.22124 6.3301L10.2293 8.83003L10.2294 8.83L9.22124 6.3301ZM7.16019 7.1612L6.15212 4.66128L6.152 4.66133L7.16019 7.1612ZM7.16019 7.56589L8.16847 5.06606L8.16838 5.06602L7.16019 7.56589ZM9.22136 8.39725L8.21308 10.8971L8.21322 10.8971L9.22136 8.39725ZM12.8203 12.3326L10.2661 13.194L10.2661 13.1941L12.8203 12.3326ZM13.5804 14.5865L11.0263 15.4479L11.0263 15.448L13.5804 14.5865ZM13.9505 14.5865L16.5047 15.448L16.5047 15.4479L13.9505 14.5865ZM14.7107 12.3326L17.2649 13.1941L17.2649 13.194L14.7107 12.3326ZM18.3096 8.39725L19.3176 10.8972L19.3179 10.8971L18.3096 8.39725ZM20.3708 7.56589L19.3626 5.06602L19.3625 5.06606L20.3708 7.56589ZM20.4997 7.36355H23.1952H20.4997ZM21.3788 4.6613L19.3178 3.8302L17.3016 8.83001L19.3625 9.66111L21.3788 4.6613ZM19.3178 3.83018C18.4353 3.47431 17.6486 2.67147 17.2649 1.53345L12.1565 3.25601C13.0031 5.7668 14.8471 7.84023 17.3016 8.83002L19.3178 3.83018ZM17.2648 1.53324L16.5047 -0.720493L11.3964 1.00248L12.1565 3.25622L17.2648 1.53324ZM16.5047 -0.72048C16.1472 -1.78027 15.1275 -2.69552 13.7655 -2.69552V2.69552C12.5686 2.69552 11.6967 1.89282 11.3964 1.00247L16.5047 -0.72048ZM13.7655 -2.69552C12.4034 -2.69552 11.3837 -1.78027 11.0263 -0.72048L16.1346 1.00247C15.8343 1.89282 14.9623 2.69552 13.7655 2.69552V-2.69552ZM11.0263 -0.720534L10.2661 1.53308L15.3744 3.25613L16.1346 1.00252L11.0263 -0.720534ZM10.2661 1.53323C9.88222 2.6715 9.09551 3.47436 8.21311 3.83021L10.2294 8.83C12.6838 7.84019 14.5277 5.76677 15.3745 3.25598L10.2661 1.53323ZM8.21318 3.83018L6.15212 4.66128L8.16826 9.66113L10.2293 8.83003L8.21318 3.83018ZM6.152 4.66133C4.9507 5.14581 4.33573 6.29066 4.33573 7.36355H9.72677C9.72677 8.25581 9.21472 9.23909 8.16838 9.66108L6.152 4.66133ZM4.33573 7.36355C4.33573 8.43644 4.9507 9.58129 6.152 10.0658L8.16838 5.06602C9.21472 5.488 9.72677 6.47128 9.72677 7.36355H4.33573ZM6.1519 10.0657L8.21308 10.8971L10.2296 5.89741L8.16847 5.06606L6.1519 10.0657ZM8.21322 10.8971C9.09557 11.253 9.88222 12.0558 10.2661 13.194L15.3744 11.4712C14.5277 8.96063 12.684 6.88719 10.2295 5.89735L8.21322 10.8971ZM10.2661 13.1941L11.0263 15.4479L16.1346 13.725L15.3744 11.4712L10.2661 13.1941ZM11.0263 15.448C11.3837 16.5077 12.4034 17.423 13.7655 17.423V12.032C14.9623 12.032 15.8343 12.8347 16.1346 13.725L11.0263 15.448ZM13.7655 17.423C15.1275 17.423 16.1472 16.5077 16.5047 15.448L11.3964 13.725C11.6967 12.8347 12.5686 12.032 13.7655 12.032V17.423ZM16.5047 15.4479L17.2649 13.1941L12.1565 11.4712L11.3964 13.725L16.5047 15.4479ZM17.2649 13.194C17.6488 12.0557 18.4355 11.2529 19.3176 10.8972L17.3016 5.89729C14.8469 6.88703 13.0032 8.96075 12.1565 11.4712L17.2649 13.194ZM19.3179 10.8971L21.3791 10.0657L19.3625 5.06606L17.3013 5.89741L19.3179 10.8971ZM21.379 10.0658C22.5803 9.58129 23.1952 8.43644 23.1952 7.36355H17.8042C17.8042 6.47128 18.3162 5.488 19.3626 5.06602L21.379 10.0658ZM23.1952 7.36355C23.1952 6.28801 22.5779 5.14489 21.3789 4.66133L19.3625 9.66108C18.3184 9.24001 17.8042 8.25846 17.8042 7.36355H23.1952Z"
            fill="white"
            mask="url(#path-1-inside-1_1675_1055)"
        />
        <path
            d="M2.06401 14.8767L2.30979 14.9758C2.30983 14.9758 2.30986 14.9759 2.3099 14.9759L2.06401 14.8767ZM2.06401 14.8767C2.81444 14.5 3.42386 13.8545 3.79234 13.0494L2.06401 14.8767ZM5.6042 15.6096C5.18632 15.9257 4.81995 16.3068 4.51536 16.7366C4.21076 16.3068 3.8444 15.9257 3.42654 15.6096C3.84442 15.2935 4.2108 14.9124 4.51539 14.4826C4.81999 14.9124 5.18635 15.2935 5.6042 15.6096Z"
            fill="white"
            stroke="white"
            strokeWidth="2.69552"
        />
    </svg>
);

export const WhiteLogo = ({ className }: WithClassName) => (
    <svg
        aria-hidden="true"
        className={className}
        width="1390"
        height="240"
        viewBox="0 0 1390 240"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <mask
            id="path-1-inside-1_13670_20420"
            fill="white"
        >
            <rect
                x="274.805"
                width="1114.67"
                height="239.908"
                rx="12.6316"
            />
        </mask>
        <rect
            x="274.805"
            width="1114.67"
            height="239.908"
            rx="12.6316"
            stroke="white"
            strokeWidth="30.1404"
            mask="url(#path-1-inside-1_13670_20420)"
        />
        <path
            d="M439.847 44.1477V72.861H398.735V196.415H368.716V72.861H327.821V44.1477L439.847 44.1477ZM486.623 167.702H550.793V196.415H456.605V44.1477L549.706 44.1477V72.861L486.623 72.861V105.272H544.268V133.551H486.623V167.702ZM720.958 44.1477V196.415H691.157V97.6589L648.304 168.355H644.824L601.971 97.8764V196.415L571.953 196.415V44.1477H602.841L646.564 116.149L690.069 44.1477L720.958 44.1477ZM806.103 44.1477C820.75 44.1477 833.077 49.0783 843.083 58.9394C853.089 68.8006 858.092 80.9095 858.092 95.2661C858.092 109.623 853.089 121.732 843.083 131.593C833.077 141.454 820.75 146.385 806.103 146.385H779.348V196.415L749.329 196.415L749.329 44.1477L806.103 44.1477ZM806.103 118.324C812.484 118.324 817.777 116.149 821.983 111.798C826.188 107.303 828.291 101.792 828.291 95.2661C828.291 88.5954 826.188 83.0847 821.983 78.7342C817.777 74.3837 812.484 72.2085 806.103 72.2085H779.348V118.324H806.103ZM907.654 167.702L965.733 167.702V196.415L877.635 196.415V44.1477L907.654 44.1477V167.702ZM1084.92 196.415L1075.79 169.007H1015.1L1005.96 196.415H973.549L1026.84 44.1477L1064.04 44.1477L1117.55 196.415H1084.92ZM1024.67 140.946H1066.43L1045.55 78.7342L1024.67 140.946ZM1222 44.1477V72.861H1180.89V196.415H1150.87V72.861H1109.98V44.1477L1222 44.1477ZM1268.78 167.702L1332.95 167.702V196.415L1238.76 196.415V44.1477L1331.86 44.1477V72.861H1268.78V105.272H1326.42V133.551L1268.78 133.551V167.702Z"
            fill="white"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.6316 0H227.368C234.345 0 240 5.65535 240 12.6316V227.276C240 234.252 234.345 239.908 227.368 239.908H12.6316C5.65535 239.908 0 234.252 0 227.276V12.6316C0 5.65534 5.65535 0 12.6316 0ZM81.8408 92.7085C96.3379 92.7085 107.04 89.2161 113.506 83.7767V83.7968V163.32H74.0501V192.443H180.931V163.32H143.705V39.9609H120.192C113.506 55.6567 102.803 64.1469 80.5156 64.1469H74.7127V92.7085H81.8408Z"
            fill="white"
        />
    </svg>
);

export const ShortenIcon = ({ className }: WithClassName) => {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <path
                d="M21 9H3"
                stroke={`rgb(var(--primary-700))`}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M15 15H3"
                stroke={`rgb(var(--primary-700))`}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};

export const InsertBelowIcon = ({ className }: WithClassName) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M16 12H3" />
        <path d="M16 6H3" />
        <path d="M21 6v10a2 2 0 0 1-2 2h-5" />
        <path d="m16 16-2 2 2 2" />
    </svg>
);

export const GradientSendIcon = ({ className }: WithClassName) => {
    const { primaryFrom, primaryTo } = getGradientColours();

    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <path
                d="M22 2L11 13"
                stroke="url(#paint0_linear_15943_43548)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M22 2L15 22L11 13L2 9L22 2Z"
                stroke="url(#paint1_linear_15943_43548)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <defs>
                <linearGradient
                    id="paint0_linear_15943_43548"
                    x1="11"
                    y1="7.5"
                    x2="22"
                    y2="7.5"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
                <linearGradient
                    id="paint1_linear_15943_43548"
                    x1="2"
                    y1="12"
                    x2="22"
                    y2="12"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor={primaryFrom} />
                    <stop
                        offset="1"
                        stopColor={primaryTo}
                    />
                </linearGradient>
            </defs>
        </svg>
    );
};
